import { type ReactNode } from "react";
import type { Service } from "@oathera/contract";

/*
 * Decorative, theme-aware SVG icons for the Overview KPI tiles and the KYA service cards. They are
 * purely visual (the tile/card label is the accessible name), drawn with `currentColor` + the accent
 * token so they read correctly in light and dark with no per-theme code. The agent/AIS marks are a
 * deliberately futuristic "AI robot" head; the rest are clean line glyphs in the same style.
 *
 * All share a 24×24 viewBox, 1.6 stroke, round joins — matching the sidebar NAV_ICONS house style.
 */

function Svg(props: { children: ReactNode; fill?: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {props.children}
    </svg>
  );
}

/** Futuristic AI-robot head: antenna, rounded head, glowing eyes, side ports. */
export function AgentRobotIcon(): ReactNode {
  return (
    <Svg>
      {/* antenna */}
      <path d="M12 2.5v2.3" />
      <circle cx="12" cy="2" r="0.9" fill="currentColor" stroke="none" />
      {/* head */}
      <rect x="5" y="5" width="14" height="12" rx="4" />
      {/* eyes (accent-filled for a glow) */}
      <circle cx="9.5" cy="11" r="1.4" fill="var(--accent)" stroke="none" />
      <circle cx="14.5" cy="11" r="1.4" fill="var(--accent)" stroke="none" />
      {/* mouth bar */}
      <path d="M9.5 14.3h5" />
      {/* side ports */}
      <path d="M5 10.5H3.3M19 10.5h1.7" />
      {/* neck / shoulders */}
      <path d="M9 17v1.3a3 3 0 0 0 6 0V17" />
      <path d="M6.5 21.5h11" />
    </Svg>
  );
}

/** Apps & tools: a 2×2 app grid with a small wrench accent. */
export function AppsIcon(): ReactNode {
  return (
    <Svg>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
      <path d="M17 13.5a3 3 0 1 0 3.9 3.9l-2-.6-.6-.6-.6-2z" fill="var(--accent)" stroke="none" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
    </Svg>
  );
}

/** Intents: a target/aim with a centre accent dot. */
export function IntentsIcon(): ReactNode {
  return (
    <Svg>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.4" fill="var(--accent)" stroke="none" />
      <path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3" />
    </Svg>
  );
}

/** Interactions: two exchanging chat/flow arrows. */
export function InteractionsIcon(): ReactNode {
  return (
    <Svg>
      <path d="M4 8h11l-2.5-2.5M4 8l2.5 2.5" />
      <path d="M20 16H9l2.5 2.5M20 16l-2.5-2.5" />
    </Svg>
  );
}

/** Threats: a shield with an alert mark. */
export function ThreatsIcon(): ReactNode {
  return (
    <Svg>
      <path d="M12 2.5l7.5 2.8V11c0 5-3.3 7.8-7.5 9.5C7.8 18.8 4.5 16 4.5 11V5.3z" />
      <path d="M12 8v4" />
      <circle cx="12" cy="15" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Reliability/availability: a heartbeat pulse across a dial. */
export function ReliabilityIcon(): ReactNode {
  return (
    <Svg>
      <path d="M3.5 12h3l2-5 3 10 2.5-7 1.5 2h4.5" stroke="var(--accent)" />
      <path d="M3.5 18.5h17" />
    </Svg>
  );
}

/** IDP (OpenID Provider): an identity key / badge. */
export function IdpIcon(): ReactNode {
  return (
    <Svg>
      <circle cx="8.5" cy="9" r="3.2" />
      <path d="M8.5 12.2V17l1.6 1.6-1.6 1.6" />
      <path d="M11.6 9h8.9M17 9v2.4M14.3 9v1.8" />
      <circle cx="8.5" cy="8.6" r="0.8" fill="var(--accent)" stroke="none" />
    </Svg>
  );
}

/** AIS (Agent Identity Service): an AI-robot identity mark. */
export function AisIcon(): ReactNode {
  return (
    <Svg>
      <path d="M12 2.6v1.8" />
      <rect x="5.5" y="4.5" width="13" height="10.5" rx="3.5" />
      <circle cx="9.8" cy="9.6" r="1.2" fill="var(--accent)" stroke="none" />
      <circle cx="14.2" cy="9.6" r="1.2" fill="var(--accent)" stroke="none" />
      <path d="M9.8 12.4h4.4" />
      <path d="M8 18.6a4 4 0 0 1 8 0" />
      <path d="M6.5 21.3h11" />
    </Svg>
  );
}

/** VCI (Verifiable Credential Issuer): a certificate with a seal. */
export function VciIcon(): ReactNode {
  return (
    <Svg>
      <rect x="3.5" y="4" width="17" height="12" rx="2" />
      <path d="M6.5 8h7M6.5 11h5" />
      <circle cx="16.5" cy="16.5" r="3" fill="var(--accent)" stroke="none" />
      <path d="M15 19l-1.2 3 2.7-1.4 2.7 1.4L18 19" />
    </Svg>
  );
}

/** Gateway MCP: a routing gateway node — a hub with branching connections. */
export function GatewayIcon(): ReactNode {
  return (
    <Svg>
      <circle cx="12" cy="12" r="2.4" fill="var(--accent)" stroke="none" />
      <circle cx="4" cy="5" r="1.8" />
      <circle cx="4" cy="19" r="1.8" />
      <circle cx="20" cy="5" r="1.8" />
      <circle cx="20" cy="19" r="1.8" />
      <path d="M5.6 6.1 10 10.4M18.4 6.1 14 10.4M5.6 17.9 10 13.6M18.4 17.9 14 13.6" />
    </Svg>
  );
}

/** Identity MCP: two MCP plug-ends connecting (a model-context link). */
export function IdentityMcpIcon(): ReactNode {
  return (
    <Svg>
      <path d="M3.5 12h5" />
      <path d="M15.5 12h5" />
      <rect x="8" y="8.5" width="3.5" height="7" rx="1.4" />
      <rect x="12.5" y="8.5" width="3.5" height="7" rx="1.4" fill="var(--accent)" stroke="none" />
      <path d="M9.75 6.3v2.2M9.75 15.5v2.2M14.25 6.3v2.2M14.25 15.5v2.2" />
    </Svg>
  );
}

/** Resource Server(s): a stack of server units. */
export function ResourceIcon(): ReactNode {
  return (
    <Svg>
      <rect x="4" y="4" width="16" height="5.5" rx="1.4" />
      <rect x="4" y="14.5" width="16" height="5.5" rx="1.4" />
      <circle cx="7.5" cy="6.75" r="0.9" fill="var(--accent)" stroke="none" />
      <circle cx="7.5" cy="17.25" r="0.9" fill="var(--accent)" stroke="none" />
      <path d="M11 6.75h6M11 17.25h6" />
    </Svg>
  );
}

/**
 * A small LIVE health-monitoring graph that mimics the look of the Overview's "Decisions trend"
 * chart: a jagged time-series LINE with a soft area fill beneath it, auto-scrolling right-to-left
 * infinitely for a "live monitor" feel next to a Healthy status. Each service gets a DIFFERENT but
 * deterministic trend (seeded by `service`) so no two monitors look alike. The series spans exactly
 * TILE_W units and its first and last samples are equal, so the two back-to-back copies join and
 * the scroll loops seamlessly. Colour follows the parent via `currentColor` (the healthy status
 * token). Honours reduced motion (the CSS animation is disabled by the theme's reduced-motion rule).
 */

/** Tile width = the scroll distance; two copies at this spacing make a seamless loop. */
const TILE_W = 48;
/** Number of samples across one tile (a denser, chart-like jagged line, not a smooth wave). */
const PULSE_SAMPLES = 16;
/** Vertical band the trend varies within (viewBox is 16 tall; keep a margin top and bottom). */
const PULSE_TOP = 3;
const PULSE_BOTTOM = 13;

/** A tiny deterministic PRNG (mulberry32) so each service's trend is stable across renders. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash a service id to a stable numeric seed. */
function seedFor(service: string): number {
  let h = 0;
  for (let i = 0; i < service.length; i++) h = (h * 31 + service.charCodeAt(i)) | 0;
  return h;
}

/**
 * Build one service's trend as a jagged list of `[x,y]` samples across a tile. The first and last
 * y are forced equal so the looped copies connect without a step. The jaggedness (random walk with
 * small steps, lightly mean-reverting) reads like a real metric trend rather than a sine wave.
 */
function trendSamples(service: string): ReadonlyArray<readonly [number, number]> {
  const rand = seeded(seedFor(service));
  const mid = (PULSE_TOP + PULSE_BOTTOM) / 2;
  const span = (PULSE_BOTTOM - PULSE_TOP) / 2;
  const pts: Array<[number, number]> = [];
  let y = mid + (rand() - 0.5) * span; // start somewhere in-band
  for (let i = 0; i < PULSE_SAMPLES; i++) {
    const x = (i / (PULSE_SAMPLES - 1)) * TILE_W;
    // Random walk with a pull back toward the midline, clamped to the band.
    const step = (rand() - 0.5) * span * 0.9;
    const revert = (mid - y) * 0.18;
    y = Math.min(PULSE_BOTTOM, Math.max(PULSE_TOP, y + step + revert));
    pts.push([x, y]);
  }
  // Force a seamless loop: last sample equals the first.
  pts[pts.length - 1] = [TILE_W, pts[0]![1]];
  return pts;
}

/** The stroked line path `M … L …` through the samples. */
function linePath(pts: ReadonlyArray<readonly [number, number]>): string {
  return pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(" ");
}

/** The filled-area path: the line, then down to the baseline and back, closed. */
function areaPath(pts: ReadonlyArray<readonly [number, number]>): string {
  const first = pts[0]!;
  const last = pts[pts.length - 1]!;
  return `${linePath(pts)} L${last[0].toFixed(2)} 16 L${first[0].toFixed(2)} 16 Z`;
}

export function HealthPulseIcon(props: { service?: Service }): ReactNode {
  const pts = trendSamples(props.service ?? "default");
  const line = linePath(pts);
  const area = areaPath(pts);
  const copyW = TILE_W; // the scroll distance (one tile)
  return (
    <svg
      className="ov-ekg"
      viewBox={`0 0 ${TILE_W * 2} 16`}
      width="96"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="ov-ekg-fade" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="currentColor" stopOpacity="0" />
          <stop offset="0.14" stopColor="currentColor" stopOpacity="1" />
          <stop offset="0.86" stopColor="currentColor" stopOpacity="1" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        <mask id="ov-ekg-mask">
          <rect x="0" y="0" width={TILE_W * 2} height="16" fill="url(#ov-ekg-fade)" />
        </mask>
      </defs>
      <g className="ov-ekg__scroll" style={{ ["--ekg-shift" as string]: `-${copyW}px` }} mask="url(#ov-ekg-mask)">
        {/* Two copies back-to-back (second shifted one tile right) so the loop is seamless as the
            group scrolls one tile left. Each copy = a soft area fill under the trend line. */}
        <path className="ov-ekg__area" d={area} fill="currentColor" stroke="none" />
        <path className="ov-ekg__line" d={line} />
        <g transform={`translate(${copyW} 0)`}>
          <path className="ov-ekg__area" d={area} fill="currentColor" stroke="none" />
          <path className="ov-ekg__line" d={line} />
        </g>
      </g>
    </svg>
  );
}

